/**
 * Seeded randomness for printable worksheets.
 *
 * A worksheet is a pure function of its settings and a seed: the same pair always gives
 * the same problems, so a teacher can note the seed printed on the page and regenerate
 * the sheet (or its answer key) later. The generator is a Park–Miller "minimal standard"
 * LCG — plain double arithmetic, exact below 2^53 — fed a mixed seed so that consecutive
 * seeds do not produce correlated first values.
 *
 * Never call `newSeed` during render: the first render is static HTML. Widgets start from
 * `DEFAULT_SEED` and draw a fresh one inside a click handler.
 */

export type Rng = () => number;

export const SEED_MIN = 1;
export const SEED_MAX = 999_999;
/** The seed every widget renders with before anyone presses "New worksheet". */
export const DEFAULT_SEED = 240_817;

const MODULUS = 2_147_483_647;
const MULTIPLIER = 48_271;
const MIX = 2_654_435_761;
const WARM_UP = 6;
const SEED_TEXT = /^\d{1,6}$/;

/** A seed typed or stored as anything becomes a whole number in `[SEED_MIN, SEED_MAX]`. */
export function normalizeSeed(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_SEED;
  }
  const whole = Math.abs(Math.trunc(value));
  const wrapped = whole % SEED_MAX;
  return wrapped < SEED_MIN ? SEED_MIN : wrapped;
}

/** Reads a typed seed; anything that is not a positive whole number is rejected. */
export function parseSeed(raw: string): number | null {
  const text = raw.trim();
  if (!SEED_TEXT.test(text)) {
    return null;
  }
  const value = Number(text);
  return value >= SEED_MIN && value <= SEED_MAX ? value : null;
}

export function createRng(seed: number): Rng {
  let state = (normalizeSeed(seed) * MIX + 12_345) % MODULUS;
  if (state === 0) {
    state = 1;
  }
  const next = () => {
    state = (state * MULTIPLIER) % MODULUS;
    return (state - 1) / (MODULUS - 1);
  };
  for (let i = 0; i < WARM_UP; i += 1) {
    next();
  }
  return next;
}

/** A fresh seed from the platform CSPRNG. Browser event handlers only. */
export function newSeed(): number {
  const buffer = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buffer);
  return SEED_MIN + ((buffer[0] ?? 0) % (SEED_MAX - SEED_MIN + 1));
}

/** An integer in `[min, max]`, both ends included. */
export function randInt(rng: Rng, min: number, max: number): number {
  const low = Math.ceil(Math.min(min, max));
  const high = Math.floor(Math.max(min, max));
  return Math.min(high, low + Math.floor(rng() * (high - low + 1)));
}

export function pick<T>(rng: Rng, items: readonly T[]): T {
  return items[randInt(rng, 0, items.length - 1)] as T;
}

/** Fisher–Yates shuffle into a new array. */
export function shuffled<T>(rng: Rng, items: readonly T[]): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = randInt(rng, 0, i);
    const held = result[i] as T;
    result[i] = result[j] as T;
    result[j] = held;
  }
  return result;
}

/** The seed as shown on the page: six digits, so "42" reads "000042". */
export function formatSeed(seed: number): string {
  return String(normalizeSeed(seed)).padStart(6, "0");
}
